<template>
  <div v-if="privacyPageUuid" v-html="page.content"></div>
  <div v-else>
    <h3>What matters to us?</h3>
    <p>The Privacy Policy document is an expression of our commitment to protecting the rights of persons using this service,
      which provides access
      to an account in the loyalty program <span v-encode="title"></span> organized by <span
          v-encode="adminName"></span>.</p>
    <p>At the same time, the document fulfils the information obligation arising from Article 13 of Regulation
      (EU) No 2016/679 of the European Parliament and of the Council of 27 April 2016 on the protection of
      natural persons with regard to the processing of personal data and on the free movement of such data,
      and repealing Directive 95/46/EC (General Data Protection Regulation) (OJ L119, 4.05.2016, p. 1)
      (hereinafter "GDPR").</p>
    <p>We hope that in this Privacy Policy you will find answers to all questions regarding the protection of your
      personal data. If you have any further questions, you may always contact us at the correspondence
      address {{ contactEmail }} or visit one of our physical locations.</p>
    <h3>Who is the controller of your personal data?</h3>
    <p><strong></strong>The controller of your personal data is <span v-encode="adminName"></span>.</p>
    <h3 v-if="entity.iodoEmail">How can you contact our Data Protection Officer?</h3>
    <p v-if="entity.iodoEmail">We have appointed a Data Protection Officer in our organisation, whom you may contact
      in all matters related to the processing of personal data by sending an email to: {{ entity.iodoEmail }}.</p>
    <h3>How do we obtain your data?</h3>
    <p>Your personal data are obtained when you register in our loyalty program.</p>
    <h3>What is the legal basis, purpose and scope?</h3>
    <p>We process your personal data to the extent necessary for the purposes for which they are processed, which in turn
      determines the legal basis legitimising their processing.</p>
    <p><strong>Loyalty program operation</strong></p>
    <p><em>Categories of data<br/> </em>The data we collect for this purpose include your identification data (first and last name, company name), contact details (email address, telephone), address information, date of birth and gender.</p>
    <p><em>Legal basis<br/> </em>Article 6(1)(b) GDPR – necessary for the performance of pre-contractual measures at your request and for the performance of the contract.</p>
    <p><strong>Communication (responses to inquiries, complaints and requests)</strong></p>
    <p><em>Categories of data<br/> </em>Your identification and contact details, information about your use of our products and services, data relating to your activity in the loyalty program, and any data forming the subject of your inquiries, complaints or requests.</p>
    <p><em>Legal basis</em><br/>Article 6(1)(f) GDPR – our legitimate interest in remaining in contact with you and providing assistance/answers to your enquiries.</p>
    <p><strong>Marketing</strong></p>
    <p><em>Categories of data<br/> </em>Your identification and contact information and data concerning your activity in the loyalty program.</p>
    <p><em>Legal basis<br/> </em>Article 6(1)(a) GDPR – consent to the processing of personal data. Providing such data is voluntary but may be necessary for performance of the contract.</p>
    <h3>How long do we retain your data?</h3>
    <p>
      <strong>Loyalty program operation</strong><br>
      Until you withdraw from participation in the loyalty program (subject to the time necessary to establish,
      pursue or defend claims) or until the data are no longer necessary for the purposes for which they were collected.
    </p>
    <p>
      <strong>Communication (responses to inquiries, complaints and requests)</strong><br>
      For the time necessary to establish, pursue or defend claims.
    </p>
    <p>
      <strong>Marketing</strong><br>
      Until consent is withdrawn or the data are no longer necessary for the intended purpose.
    </p>
    <p v-if="showCookiesInfo">
      <strong>Cookies</strong><br>
      Until you change your preferences in the settings or the data are no longer necessary for the stated purpose.
    </p>
    <h3>What rights do you have as a Data Subject?</h3>
    <p>Pursuant to Articles 7, 15–22 and 77 GDPR, every user is entitled to the following rights:</p>
    <p><strong>Right of access (Art. 15 GDPR)<br/> </strong>The data subject has the right to obtain from the controller confirmation as to whether or not personal data concerning him or her are being processed and, where that is the case, access to the personal data and information about processing. In accordance with Art. 15, the controller shall provide the data subject with a copy of the personal data undergoing processing.</p>
    <p><strong>Right to rectification (Art. 16 GDPR)<br/> </strong>The data subject has the right to obtain from the controller without undue delay the rectification of inaccurate personal data concerning him or her.</p>
    <p><strong>Right to erasure ("right to be forgotten") (Art. 17 GDPR)<br/>
    </strong>The data subject has the right to obtain from the controller the erasure of personal data concerning him or her without undue delay where one of the grounds applies, including:</p>
    <ul>
      <li>the personal data are no longer necessary in relation to the purposes for which they were collected or otherwise processed,</li>
      <li>the data subject withdraws consent on which the processing is based,</li>
      <li>the data subject objects to the processing pursuant to Article 21(1) and there are no overriding legitimate grounds for the processing.</li>
    </ul>
    <p><strong>Right to restriction of processing (Art. 18 GDPR)<br/> </strong>The data subject has the right to obtain from the controller restriction of processing in the following cases:</p>
    <ul>
      <li>where the accuracy of the personal data is contested – for the period enabling the controller to verify the accuracy,</li>
      <li>where the data subject has objected to processing pursuant to Article 21(1) – pending the verification whether the controller's legitimate grounds override those of the data subject,</li>
      <li>where processing is unlawful and the data subject objects to erasure and requests restriction instead.</li>
    </ul>
    <p><strong>Right to data portability (Art. 20 GDPR)<br/> </strong>You have the right to receive the personal data you have provided to us in a structured, commonly used and machine-readable format and to transmit those data to another controller where technically feasible. The exercise of this right may be limited if it adversely affects the rights and freedoms of others.</p>
    <p><strong>Right to object to processing (Art. 21 GDPR)<br/> </strong>You have the right at any time to object to processing of personal data concerning you, including profiling, where processing is based on legitimate interests. If your objection is upheld and no other legal basis for the processing applies, we will cease processing your personal data for those purposes. In the case of direct marketing, processing will stop immediately upon objection.</p>
    <p><strong>Right to withdraw consent (Art. 7 GDPR)<br/> </strong>Consent to the processing of personal data given at any time may be withdrawn. Withdrawal of consent does not affect the lawfulness of processing carried out prior to its withdrawal.</p>
    <p><strong>Right to lodge a complaint with a Supervisory Authority (Art. 77 GDPR)<br/>
    </strong>If you consider that the processing of your personal data infringes applicable law, you have the right to lodge a complaint with the competent supervisory authority, e.g. the President of the Personal Data Protection Office at ul. Stawki 2, Warsaw 00-193.</p>
    <h3>How to exercise your rights?</h3>
    <p>To exercise any of the rights listed above, you may contact us in writing at the correspondence address {{ contactEmail }} or visit one of our physical branches.</p>
    <p>In addition, the rights of access and rectification may be exercised via this service.</p>
    <h3>How do we protect your data?</h3>
    <p>We have taken appropriate technical and organisational measures to ensure the security of the personal data entrusted to us.</p>
    <h3>To whom do we disclose your data?</h3>
    <p>It is necessary for us to use the services of external entities.
      We only use processors that provide sufficient guarantees of implementing
      appropriate technical and organisational measures to ensure that processing meets the requirements
      of the GDPR and protects the rights of data subjects. We disclose data only where necessary for the
      performance of a given processing purpose and only to the extent required to carry out that purpose.</p>
    <p>Recipients of your data are entities acting solely on our instructions and providing services
      necessary for the operation of our company, including the loyalty program of which you are a participant.</p>
    <p>We may also disclose your data in response to lawful requests from public authorities to the extent
      required by law.</p>

    <h3>Can the Privacy Policy be changed?</h3>
    <p>We reserve the right to amend this Privacy Policy. We will notify our Clients of any changes by email without undue delay.</p>
    <h3 v-if="termsPageUuid">Are there other policies we have implemented?</h3>
    <p v-if="termsPageUuid">In addition to this Privacy Policy, the rules for the processing of personal data are described in the
      <a href="#" class="m-nav__link" @click="openTermsModal">
        <span class="m-nav__link-text ">Terms and Conditions</span>
      </a>
      .
    </p>
    <h3>Cookies</h3>
    <div v-if="showCookiesInfo">
      <p>
        This website uses cookies, which are small text files stored on your end device.
      </p>

      <p v-if="isGoogleAnalyticsEnabled">
        For analytical purposes we use the Google Analytics service provided by Google LLC, 1600
        Amphitheatre Parkway, Mountain View, CA 94043, USA. For details on data processing in Google Analytics, please refer to Google's explanations: <a href="https://support.google.com/analytics/answer/6004245" target="_blank">https://support.google.com/analytics/answer/6004245</a>.
      </p>

      <p v-if="isFbPixelEnabled">
        For marketing purposes we use the Facebook Pixel provided by Facebook Inc., 1601 S.
        California Ave. Palo Alto, CA 94304, USA or Facebook Ireland Ltd., 4 Grand Canal Square, Grand Canal Harbour,
        Dublin 2, Ireland. For additional information please consult Facebook's privacy policy:
        <a href="https://www.facebook.com/privacy/explanation" target="_blank">https://www.facebook.com/privacy/explanation</a>.
      </p>

      <p>
        You may block cookies at any time by changing your preferences in your browser settings.
      </p>
    </div>
    <p v-else>This website does not use any cookies.</p>
  </div>
</template>

<script>
import Encode from "@/directives/encode";
import {mapActions, mapState} from 'vuex'
import {PAGE} from "@/utils/PageModalTypes";

export default {
  name: "PrivacyPolicyContent",
  directives: {Encode},
  props: {
    termsInModal: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      page: {
        content: null,
      }
    };
  },
  mounted() {
    if (this.privacyPageUuid) {
      this.getPage('privacy/' + this.entityUuid);
    }
  },
  methods: {
    ...mapActions([
      'setPageModal'
    ]),
    openTermsModal() {
      if (this.termsInModal) {
        this.setPageModal({
          show: true,
          type: PAGE,
          title: 'Regulamin',
          content: 'terms/' + this.entityUuid
        });
      } else {
        this.$router.push({name: 'CustomPage', params: {pageUuid: 'terms'}})
      }
    },
    getPage(uuid) {
      this.$http.get('/page/' + uuid)
          .then((r) => {
            this.page = r.data.page
          })
    }
  },
  computed: {
    ...mapState({
      entityUuid: state => state.auth.entity.uuid,
      entity: state => state.settings.entity,
      title: state => state.settings.title,
      termsPageUuid: state => state.settings.termsPageUuid,
      contactEmail: state => state.settings.contactEmail,
      privacyPageUuid: state => state.settings.privacyPageUuid,
      analytics: state => state.settings.analytics,
    }),
    adminName() {
      let name;

      if (this.entity.billingData.companyName !== null)
        name = this.entity.billingData.companyName;
      else
        name = this.entity.billingData.firstName + ' ' + this.entity.billingData.lastName;

      if (this.entity.billingData.street !== null)
        name += ', ul. ' + this.entity.billingData.street;

      if (this.entity.billingData.postCode !== null || this.entity.billingData.city !== null)
        name += ', ' + this.entity.billingData.postCode + ' ' + this.entity.billingData.city;

      if (this.entity.billingData.nip !== null)
        name += ', NIP: ' + this.entity.billingData.nip;

      return name;
    },
    showCookiesInfo() {
      return this.isGoogleAnalyticsEnabled || this.isFbPixelEnabled;
    },
    isGoogleAnalyticsEnabled() {
      return this.analytics.ua || this.analytics.ga4;
    },
    isFbPixelEnabled() {
      return this.analytics.fbp;
    }
  }
}
</script>

<style scoped>

</style>
